"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

import { logger } from "@/lib/logger";

/**
 * Listens for Supabase recovery links (#access_token…) on any page,
 * establishes a session, and routes the user to the reset-password screen.
 */
export function PasswordRecoveryHandler() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const { hash, pathname, search } = window.location;
    if (!hash || hash.length <= 1) {
      return;
    }

    const params = new URLSearchParams(hash.slice(1));
    const errorCode = params.get("error_code");
    const errorDescription = params.get("error_description");
    if (errorCode) {
      logger.warn("Supabase recovery error", { errorCode, errorDescription });
      const errorParams = new URLSearchParams({
        error: errorCode,
      });
      if (errorDescription) {
        errorParams.set("message", errorDescription);
      }
      window.history.replaceState({}, document.title, `${pathname}${search}`);
      router.replace(`/auth/reset-password?${errorParams.toString()}`);
      return;
    }

    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (type !== "recovery" || !accessToken || !refreshToken) {
      return;
    }

    const cleanUrl = `${pathname}${search}`;
    window.history.replaceState({}, document.title, cleanUrl);

    // Loaded only when a recovery link is present: this handler is mounted on
    // every route, and a static import put the Supabase client (plus the zod
    // env schema behind it) into every page's first-load bundle.
    void import("@/lib/supabase/client")
      .then(({ createClient }) =>
        createClient().auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }),
      )
      .then(({ error }) => {
        if (error) {
          logger.error("Failed to set recovery session", error);
          router.replace("/auth/login?error=recovery_failed");
          return;
        }
        router.replace("/auth/reset-password");
      })
      .catch((error: unknown) => {
        logger.error(
          "Unexpected recovery session error",
          error instanceof Error ? error : { error },
        );
        router.replace("/auth/login?error=recovery_failed");
      });
  }, [router]);

  return null;
}
